import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChalkCartons() {
  const [cartons, setCartons] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingCarton, setEditingCarton] = useState(null);
  const [formData, setFormData] = useState({
    batch_number: '',
    product_name: '',
    quantity: 0,
    cartons_per_batch: 0,
    production_date: '',
    expiry_date: '',
    quality_status: 'approved'
  });

  useEffect(() => {
    fetchCartons();
  }, []);

  const fetchCartons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chalk-cartons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCartons(response.data);
    } catch (error) {
      toast.error('Failed to fetch cartons');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingCarton) {
        await axios.put(`${API}/chalk-cartons/${editingCarton.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Carton updated successfully');
      } else {
        await axios.post(`${API}/chalk-cartons`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Carton added successfully');
      }
      setOpen(false);
      resetForm();
      fetchCartons();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Operation failed';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const resetForm = () => {
    setFormData({
      batch_number: '',
      product_name: '',
      quantity: 0,
      cartons_per_batch: 0,
      production_date: '',
      expiry_date: '',
      quality_status: 'approved'
    });
    setEditingCarton(null);
  };

  const openEditDialog = (carton) => {
    setEditingCarton(carton);
    setFormData({
      batch_number: carton.batch_number,
      product_name: carton.product_name,
      quantity: carton.quantity,
      cartons_per_batch: carton.cartons_per_batch,
      production_date: carton.production_date,
      expiry_date: carton.expiry_date || '',
      quality_status: carton.quality_status
    });
    setOpen(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      approved: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.approved;
  };

  return (
    <div data-testid="chalk-cartons-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Chalk Cartons Inventory</h1>
          <p className="text-slate-600 mt-1">Track finished product inventory</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-carton-button" onClick={resetForm}>
              <Plus className="mr-2" size={16} />
              Add Carton Batch
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="carton-dialog" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCarton ? 'Edit Carton Batch' : 'Add Carton Batch'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="batch_number">Batch Number *</Label>
                <Input
                  id="batch_number"
                  data-testid="batch-number-input"
                  value={formData.batch_number}
                  placeholder="CK-DDMMYY (auto from production date if empty)"
                  onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="product_name">Product Name *</Label>
                <Input
                  id="product_name"
                  data-testid="product-name-input"
                  value={formData.product_name}
                  onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="quantity">Quantity *</Label>
                  <Input
                    id="quantity"
                    data-testid="quantity-input"
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cartons_per_batch">Cartons/Batch *</Label>
                  <Input
                    id="cartons_per_batch"
                    data-testid="cartons-per-batch-input"
                    type="number"
                    value={formData.cartons_per_batch}
                    onChange={(e) => setFormData({ ...formData, cartons_per_batch: parseInt(e.target.value) })}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="production_date">Production Date *</Label>
                  <Input
                    id="production_date"
                    data-testid="production-date-input"
                    type="date"
                    value={formData.production_date}
                    onChange={(e) => {
                    const v = e.target.value;
                    let batch = formData.batch_number;
                    if (!batch && v) {
                      const d = new Date(v);
                      batch = `CK-${String(d.getDate()).padStart(2, '0')}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getFullYear()).slice(-2)}`;
                    }
                    setFormData({ ...formData, production_date: v, batch_number: batch });
                  }}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expiry_date">Expiry Date</Label>
                  <Input
                    id="expiry_date"
                    data-testid="expiry-date-input"
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="quality_status">Quality Status *</Label>
                <Select value={formData.quality_status} onValueChange={(value) => setFormData({ ...formData, quality_status: value })}>
                  <SelectTrigger data-testid="quality-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="save-carton-button" type="submit" className="w-full">
                {editingCarton ? 'Update Carton' : 'Add Carton'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Batch Number</TableHead>
              <TableHead>Product Name</TableHead>
              <TableHead>Quantity</TableHead>
              <TableHead>Cartons/Batch</TableHead>
              <TableHead>Production Date</TableHead>
              <TableHead>Expiry Date</TableHead>
              <TableHead>Quality Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cartons.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-slate-500">
                  No cartons found. Add your first batch.
                </TableCell>
              </TableRow>
            ) : (
              cartons.map((carton) => (
                <TableRow key={carton.id} data-testid={`carton-row-${carton.id}`}>
                  <TableCell className="font-medium">{carton.batch_number}</TableCell>
                  <TableCell>{carton.product_name}</TableCell>
                  <TableCell>{carton.quantity}</TableCell>
                  <TableCell>{carton.cartons_per_batch}</TableCell>
                  <TableCell>{new Date(carton.production_date).toLocaleDateString()}</TableCell>
                  <TableCell>{carton.expiry_date ? new Date(carton.expiry_date).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(carton.quality_status)} variant="outline">
                      {carton.quality_status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      data-testid={`edit-carton-${carton.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(carton)}
                    >
                      <Pencil size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
